import {css, StyleSheet} from 'aphrodite';
import React, {useEffect} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  AssemblyHistoryQuery,
  AssemblyHistoryQueryVariables,
} from 'molbio/visual-history/assembly-history/__generated/AssemblyHistoryQuery';
import {ASSEMBLY_HISTORY_TAB_CONTENTS_QUERY} from 'molbio/visual-history/assembly-history/AssemblyHistoryActions';
import VisualHistoryEventListItem from 'molbio/visual-history/VisualHistoryEventListItem';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

/**
 * Render a single creation event with assembly information.
 * @constructor
 */
export default function AssemblyHistoryTabContents({
  polymerId,
  activeVersionId,
  showingCurrentVersion,
}: {
  polymerId: string;
  activeVersionId: number;
  showingCurrentVersion: boolean;
}): JSX.Element {
  const queryResult = useBenchlingQuery<AssemblyHistoryQuery, AssemblyHistoryQueryVariables>(
    ASSEMBLY_HISTORY_TAB_CONTENTS_QUERY,
    {
      variables: {entityId: polymerId},
      renderCachedDataWhenRefetching: true,
    }
  );
  const {refetch} = queryResult;

  useEffect(() => {
    // Assembly history is always based on the current version, so no need to refetch when viewing historical versions.
    if (showingCurrentVersion) {
      refetch();
    }
  }, [activeVersionId, showingCurrentVersion, refetch]);

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (queryResult.type === QueryResultType.ERROR) {
    notifications.error('Error fetching assembly history.');
    return <></>;
  } else if (queryResult.data.assemblyHistory == null || isInaccessible(queryResult.data.assemblyHistory)) {
    return (
      <InlineBanner
        text="There was an error loading assembly history for this item."
        bannerType={InlineBannerType.ERROR}
      />
    );
  } else {
    return (
      <div className={css(styles.contentsContainer)}>
        <VisualHistoryEventListItem
          event={queryResult.data.assemblyHistory}
          onClickCreatedAt={null}
          showAsStandalone={true}
          index={0}
        />
      </div>
    );
  }
}

const styles = StyleSheet.create({
  contentsContainer: {
    padding: '12px 12px 12px 24px',
  },
});
